import { Chance } from 'chance';
import { PapiDeviation, PapiEditorText, PapiExtendedDeviation } from '@wix/da-papi-types';
import { deviationsByType } from '../deviations';

const chance = new Chance();

export type AdditionalMedia = NonNullable<PapiExtendedDeviation['additionalMedia']>[0];
export type ExtendedDeviationOriginalFile = NonNullable<PapiExtendedDeviation['originalFile']>;

/** picks certain data from a deviation (and an optional editor text caption)
 *  to create a sample datum to use as an element for deviation.extended.additionalMedia */
export function deviationToAdditionalMedia(
  deviation: PapiDeviation,
  caption?: PapiEditorText
): AdditionalMedia {
  const originalFile = deviation.extended?.originalFile ?? ({} as ExtendedDeviationOriginalFile);

  return {
    filename: `${deviation.media?.prettyName}.${originalFile.type ?? 'jpg'}`,
    fileId: deviation.deviationId,
    type: originalFile.type,
    filesize: originalFile.filesize,
    width: originalFile.width,
    height: originalFile.height,
    media: deviation.media,
    caption: caption ?? (chance.bool({ likelihood: 30 }) ? EXAMPLE_TIPTAP_CAPTION : undefined),
  } as AdditionalMedia;
}

/** build random data for a sample deviation's deviation.extended.additionalMedia */
export function generateAdditionalMedia(
  /** if undefined generates 3 to 25 media, if a number generates that amount */
  count = chance.integer({ min: 3, max: 25 })
): AdditionalMedia[] {
  // todo: image only for now
  const deviations = chance.pickset(deviationsByType.image, count);
  return deviations.map((deviation, i) => {
    const converted = deviationToAdditionalMedia(deviation);
    return {
      ...converted,
      position: i,
      filename: `${i}-${converted.filename}`,
    };
  }) as AdditionalMedia[];
}

export const EXAMPLE_TIPTAP_CAPTION = {
  excerpt: 'This is a test caption',
  html: {
    type: 'tiptap',
    markup: JSON.stringify({
      version: '1',
      document: {
        type: 'doc',
        content: [
          {
            type: 'paragraph',
            attrs: { indentType: null, indentation: null, textAlign: 'left' },
            content: [
              { type: 'text', marks: [{ type: 'underline' }], text: 'This' },
              { type: 'text', text: ' is a ' },
              { type: 'text', marks: [{ type: 'italic' }], text: 'test' },
              { type: 'text', text: ' ' },
              { type: 'text', marks: [{ type: 'bold' }], text: 'caption' },
            ],
          },
        ],
      },
      features: [],
    }),
    features: JSON.stringify([
      { type: 'TIPTAP_FEATURE', data: { tiptap: true, version: 1 } },
      { type: 'WORD_COUNT_FEATURE', data: { words: 5, chars: 22 } },
      {
        type: 'MEDIA_COUNT_FEATURE',
        data: {
          emoji: 0,
          emotes: 0,
          images: 0,
          mentions: 0,
          video: 0,
          galleries: 0,
        },
      },
      { type: 'IS_EMPTY_FEATURE', data: { isEmpty: false } },
    ]),
  },
};
